import { useUpdateCanvas } from "@/api/canvas";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { Canvas } from "@cohesive/api-client";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export function RenameCanvasDialog({
  canvas,
  detailsLoading = false,
  onClose,
  onRenamed,
}: {
  canvas: Canvas | null;
  /** When true, canvas metadata is still loading — block save so description isn't wiped. */
  detailsLoading?: boolean;
  onClose: () => void;
  onRenamed?: (name: string) => void;
}) {
  const [name, setName] = useState("");

  useEffect(() => {
    if (canvas) setName(canvas.name || "");
  }, [canvas?.id]);

  const updateCanvas = useUpdateCanvas({
    onSuccess: (updatedCanvas) => {
      toast.success("Canvas renamed");
      onRenamed?.(updatedCanvas.name);
      onClose();
    },
    onError: () => toast.error("Failed to rename canvas"),
  });

  const handleSubmit = () => {
    if (!canvas || !name.trim()) return;
    updateCanvas.mutate({
      id: canvas.id,
      // The backend's update payload treats Description as a plain string and
      // overwrites the column unconditionally (Metadata is preserved, Description
      // isn't), so omitting it wipes the existing description. Echo the current
      // value back so a rename leaves description alone.
      data: { name: name.trim(), description: canvas.description ?? "" },
    });
  };

  return (
    <Dialog
      open={canvas !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename Canvas</DialogTitle>
          <DialogDescription>
            Enter a new name for this canvas.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Canvas name"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || updateCanvas.isPending || detailsLoading}
          >
            {updateCanvas.isPending
              ? "Saving..."
              : detailsLoading
                ? "Loading..."
                : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
